import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { SidebarTagSortMode, Tag } from '../types/api';

interface UpdateTagNameParams {
  tagId: string;
  newName: string;
}

interface DeleteTagParams {
  tagId: string;
}

interface CreateTagParams {
  name: string;
  formKey: string;
}

export async function createTag({
  name,
  formKey: _formKey, // Not needed for /data/ endpoints but kept for API consistency
}: CreateTagParams): Promise<void> {
  const response = await fetch('/data/tags/create', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
    credentials: 'same-origin',
    body: JSON.stringify({ name }),
  });

  if (!response.ok) {
    try {
      const errorData = await response.json();
      if (errorData.errorMessage) {
        if (errorData.errorType === 'duplicate') {
          const error = new Error(errorData.errorMessage);
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (error as any).type = 'duplicate';
          throw error;
        }
        throw new Error(errorData.errorMessage);
      }
    } catch (parseError) {
      if (parseError instanceof Error && parseError.message) {
        throw parseError;
      }
      const errorText = await response.text().catch(() => '');
      throw new Error(errorText || `Failed to create tag: ${response.status}`);
    }
  }
}

interface UpdateTagSortParams {
  sortMode: SidebarTagSortMode;
  formKey: string;
}

const tagSortModeToBackendValue: Record<SidebarTagSortMode, number> = {
  name: 1,
  oldest: 2,
  newest: 3,
  count: 4,
  recently_used: 5,
};

export async function updateTagSort({
  sortMode,
  formKey,
}: UpdateTagSortParams): Promise<void> {
  const formData = new URLSearchParams();
  formData.append('tag_sort', tagSortModeToBackendValue[sortMode].toString());
  formData.append('form_key', formKey);

  const response = await fetch('/data/user/tag_sort', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      'X-Requested-With': 'XMLHttpRequest',
    },
    credentials: 'same-origin',
    body: formData.toString(),
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => '');
    throw new Error(errorText || `Failed to update tag sort: ${response.status}`);
  }
}

export function useUpdateTagSort() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateTagSort,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['currentUser'] });
    },
  });
}

export function useCreateTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createTag,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['currentUser'] });
      queryClient.invalidateQueries({ queryKey: ['user'] });
      queryClient.invalidateQueries({ queryKey: ['tags'] });
    },
  });
}

// Tag Management Mutations
export function useUpdateTagName() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ tagId, newName }: UpdateTagNameParams) => {
      const response = await fetch(`/data/tags/${tagId}/update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify({ name: newName }),
      });

      if (!response.ok) {
        if (response.status === 412) {
          throw new Error(
            'A tag with this name already exists. Please use a different name.',
          );
        }
        if (response.status === 400) {
          throw new Error('Invalid tag name. Please try again.');
        }
        throw new Error('Failed to update tag name');
      }

      const data = await response.json();
      return data.tag;
    },
    onSuccess: () => {
      // Invalidate all queries that might contain tags
      queryClient.invalidateQueries({ queryKey: ['currentUser'] });
      queryClient.invalidateQueries({ queryKey: ['user'] });
      queryClient.invalidateQueries({ queryKey: ['bookmarks'] });
      queryClient.invalidateQueries({ queryKey: ['bookmark'] });
      queryClient.invalidateQueries({ queryKey: ['tags'] });
    },
  });
}

export function useDeleteTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ tagId }: DeleteTagParams) => {
      const response = await fetch(`/data/tags/${tagId}/delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Tag not found or cannot be deleted.');
        }
        throw new Error('Failed to delete tag');
      }

      const data = await response.json();
      return data;
    },
    onSuccess: () => {
      // Invalidate all queries that might contain tags
      queryClient.invalidateQueries({ queryKey: ['currentUser'] });
      queryClient.invalidateQueries({ queryKey: ['user'] });
      queryClient.invalidateQueries({ queryKey: ['bookmarks'] });
      queryClient.invalidateQueries({ queryKey: ['bookmark'] });
      queryClient.invalidateQueries({ queryKey: ['tags'] });
    },
  });
}

// Helper function to check if a tag name already exists
export function tagExists(tagName: string, availableTags: Tag[]): boolean {
  return availableTags.some((tag) => tag.name.toLowerCase() === tagName.toLowerCase());
}

// Helper function to filter tags based on search input
export function filterTags(
  searchTerm: string,
  tags: Tag[],
  excludeTags: Tag[] = [],
): Tag[] {
  const excludeNames = new Set(excludeTags.map((tag) => tag.name.toLowerCase()));

  return tags.filter((tag) => {
    const tagNameLower = tag.name.toLowerCase();
    return (
      tagNameLower.includes(searchTerm.toLowerCase()) && !excludeNames.has(tagNameLower)
    );
  });
}

// Helper function to normalize tag names (lowercase and trim)
export function normalizeTag(tagName: string): string {
  return tagName.trim().toLowerCase();
}

// Helper function to sort tags by frequency of occurrence
export function sortTagsByFrequency(tags: string[]): string[] {
  // Count frequency of each tag (case-insensitive)
  const frequencyMap = new Map<string, number>();

  tags.forEach((tag) => {
    const normalized = normalizeTag(tag);
    if (normalized) {
      frequencyMap.set(normalized, (frequencyMap.get(normalized) || 0) + 1);
    }
  });

  // Convert to array and sort by frequency (descending), then alphabetically
  return Array.from(frequencyMap.entries())
    .sort(([tagA, freqA], [tagB, freqB]) => {
      if (freqB !== freqA) {
        return freqB - freqA; // Higher frequency first
      }
      return tagA.localeCompare(tagB); // Alphabetical for equal frequency
    })
    .map(([tag]) => tag);
}
